/**
 * SUNDAY FLOOR — header.js
 * Handles: mobile hide-on-scroll-down/reveal-on-scroll-up (rAF-throttled,
 * per Part H), desktop dropdown open on hover + click/focus (keyboard must
 * work), and the mobile menu drawer as a focus-trapped dialog.
 */
import { trapFocus, setBackgroundInert } from './global.js';

document.querySelectorAll('[data-sf-header]').forEach((header) => {
  initStickyReveal(header);
  initDropdowns(header);
});

document.querySelectorAll('[data-menu-drawer]').forEach((drawer) => {
  initDrawer(drawer);
});

function initStickyReveal(header) {
  if (!header.hasAttribute('data-sticky')) return;

  let lastScrollY = window.scrollY;
  let ticking = false;

  function update() {
    const currentY = window.scrollY;
    const isMobile = window.matchMedia('(max-width: 989px)').matches;

    if (isMobile && currentY > lastScrollY && currentY > header.offsetHeight) {
      header.classList.add('sf-header--hidden');
    } else {
      header.classList.remove('sf-header--hidden');
    }

    lastScrollY = currentY;
    ticking = false;
  }

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    },
    { passive: true }
  );
}

function initDropdowns(header) {
  const triggers = header.querySelectorAll('[data-dropdown-trigger]');

  triggers.forEach((trigger) => {
    const item = trigger.closest('.sf-header__nav-item');
    const panel = item.querySelector('[data-dropdown-panel]');
    if (!panel) return;

    function open() {
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
    }
    function close() {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    }

    // Hover (pointer devices only, so touch doesn't get a phantom hover-open)
    item.addEventListener('mouseenter', () => {
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) open();
    });
    item.addEventListener('mouseleave', () => {
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) close();
    });

    // Click/focus for keyboard users
    trigger.addEventListener('click', () => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      isOpen ? close() : open();
    });
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) close();
    });
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        close();
        trigger.focus();
      }
    });
  });
}

function initDrawer(drawer) {
  // The drawer is rendered as a sibling of <header>, not a descendant, so it
  // is found by matching aria-controls to the drawer's own id rather than by
  // walking DOM ancestry that doesn't exist.
  const openTrigger = document.querySelector(`[aria-controls="${drawer.id}"]`);
  const closeTargets = drawer.querySelectorAll('[data-drawer-close]');
  let removeTrap = () => {};
  let lastFocused = null;

  function open() {
    if (!drawer.hidden) return;
    lastFocused = document.activeElement;
    drawer.hidden = false;
    document.body.style.overflow = 'hidden';
    setBackgroundInert(drawer, true);
    removeTrap = trapFocus(drawer);
    const firstFocusable = drawer.querySelector('button, a, input');
    if (firstFocusable) firstFocusable.focus();
    document.addEventListener('keydown', onKeydown);
  }

  function close() {
    if (drawer.hidden) return;
    drawer.hidden = true;
    document.body.style.overflow = '';
    setBackgroundInert(drawer, false);
    removeTrap();
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused) lastFocused.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') close();
  }

  if (openTrigger) openTrigger.addEventListener('click', open);
  closeTargets.forEach((el) => el.addEventListener('click', close));
}
